import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { type Blocker } from 'react-router'
import { isEqual, cloneDeep } from 'lodash'
import { AxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { showToast, toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import {
  ChatbotApi,
  type IParamsValidateAgentByStep,
  type IAgentPayload,
  type IChatbotFullData,
  type IChatbotInstructionTemplate,
} from 'entities/Chatbot'
import {
  ChatbotInstructionType,
  type IAgentAdvancedPromptPayload,
  type IChatbotSettings,
  type IAgentAdvancedGeneralPayload,
} from 'entities/Chatbot/model/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ChatbotRoutes } from 'pages/chatbot/route/type'
import { SettingAgentStore } from 'pages/chatbot/pages/agentBookAppointment/pages/settingAgent/store/settingAgentStore'
import { ConnectCalendarStore } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/connectCalendar'
import { AgentActionsStore } from 'pages/chatbot/pages/agentBookAppointment/pages/agentActions/store/agentActionsStore'
import { AgentActivateStore } from 'pages/chatbot/pages/agentBookAppointment/pages/agentActivate/store/agentActivateStore'
import { chatbotOperation } from 'pages/chatbot/store/chatbotOperationStore'
import { AgentBookAppointmentStepsRoutes } from 'pages/chatbot/pages/agentBookAppointment'
import {
  AgentBookAppointmentRouteToStepMap,
  AgentSteps,
} from 'pages/chatbot/pages/agentBookAppointment/route/type'
import type { ChatbotStore } from 'pages/chatbot'
import { AdvancedSettingsStore } from './advancedSettingsStore'
import {
  type ISaveDraftErrorResponse,
  type IAgentData,
  type IHandleError,
  type IAgentBookAppointmentStepsStoreOptions,
} from './type'

const titleMap: Record<string, string> = {
  [AgentBookAppointmentStepsRoutes.settingAgent]: 'Setting up your Agent',
  [AgentBookAppointmentStepsRoutes.connectCalendar]: 'Connect your calendar',
  [AgentBookAppointmentStepsRoutes.actionsAgent]: 'Agent Actions',
  [AgentBookAppointmentStepsRoutes.activateAgent]: 'Activate your Agent',
}

export class AgentBookAppointmentStepsStore {
  private readonly _settingAgentStore: SettingAgentStore
  private readonly _connectCalendarStore: ConnectCalendarStore
  private readonly _agentActionsStore: AgentActionsStore
  private readonly _agentActivateStore: AgentActivateStore
  private readonly _advancedSettingsStore: AdvancedSettingsStore
  private _instruction = ''
  private _loading = true
  private _stepLoading = false
  private _dirtyAgentData: IAgentData | null = null
  private _cacheParams:
    | (Omit<IParamsValidateAgentByStep, 'step'> & {
        use_knowledge_base: boolean
      })
    | null = null

  constructor(private _chatbotStore: ChatbotStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._settingAgentStore = new SettingAgentStore(this, this._entities)
    this._connectCalendarStore = new ConnectCalendarStore(this, this._entities)
    this._agentActionsStore = new AgentActionsStore(this, this._entities)
    this._agentActivateStore = new AgentActivateStore(this, this._entities)
    this._advancedSettingsStore = new AdvancedSettingsStore(this)
  }

  init = async (id?: number, data?: IChatbotFullData) => {
    runInAction(() => {
      this._loading = true
    })

    if (id) this.agentNavigationStore.setAgentId(id)

    await Promise.all([
      this._entities.integrationsStore.calendlyIntegrationStateStore.fetchCalendlyIntegrationState(),
    ])

    if (data) {
      await Promise.all([
        this._settingAgentStore.init(data),
        this._connectCalendarStore.init(data),
        this._agentActionsStore.init(data),
        this._agentActivateStore.init(data),
        this._advancedSettingsStore.init(data?.settings),
      ])

      this._dirtyAgentData = data
      this._cacheParams = cloneDeep(this.params)
    }

    runInAction(() => {
      this._loading = false
    })
  }

  get bookingFieldsStore() {
    return this._chatbotStore.bookingFieldsStore
  }

  get stepObjectiveStore() {
    return this._chatbotStore.stepObjectiveStore
  }

  get stepQualifyStore() {
    return this._chatbotStore.stepQualifyStore
  }

  get cacheParams() {
    return this._cacheParams
  }

  get loading() {
    return this._loading
  }

  get agentNavigationStore() {
    return this._chatbotStore.agentNavigationStore
  }

  get settingAgentStore() {
    return this._settingAgentStore
  }

  get connectCalendarStore() {
    return this._connectCalendarStore
  }

  get agentActionsStore() {
    return this._agentActionsStore
  }

  get agentActivateStore() {
    return this._agentActivateStore
  }

  get chatbot() {
    return this._chatbotStore.model
  }

  get agentKnowledgeBaseStore() {
    return this._chatbotStore.agentKnowledgeBaseStore
  }

  get chatbotId() {
    return this._chatbotStore.id
  }

  get chatbotErrors() {
    return this._chatbotStore.errors
  }

  get advancedSettingsStore() {
    return this._advancedSettingsStore
  }

  get agentInstruction() {
    return this._instruction
  }

  updateInstructions = (
    newInstructions: Map<ChatbotInstructionType, IChatbotInstructionTemplate>
  ) => {
    if (newInstructions && newInstructions.size > 0) {
      runInAction(() => {
        this._instruction =
          newInstructions.get(ChatbotInstructionType.BookAppointment)?.instruction || ''
      })
    }
  }

  get params(): Omit<IParamsValidateAgentByStep, 'step'> & {
    use_knowledge_base: boolean
  } {
    return {
      ...this.settingAgentStore.stepPayload,
      ...this.connectCalendarStore.stepPayload,
      ...this.agentActionsStore.stepPayload,
      ...this.agentActivateStore.stepPayload,
      use_knowledge_base: this.chatbot.settings.knowledgeBaseUsage,
      ...this.advancedSettingsStore.params,
      settings: {
        ...this.connectCalendarStore.stepPayload.settings,
        ...this.agentActionsStore.stepPayload.settings,
      },
    }
  }

  get isExistChanges() {
    return !isEqual(this._cacheParams, this.params)
  }

  get stepLoading() {
    return this._stepLoading
  }

  get currentStep() {
    return this.agentNavigationStore.getCurrentStep
  }

  get stepStoreMap(): Record<
    string,
    SettingAgentStore | ConnectCalendarStore | AgentActionsStore | AgentActivateStore
  > {
    return {
      [AgentBookAppointmentStepsRoutes.settingAgent]: this.settingAgentStore,
      [AgentBookAppointmentStepsRoutes.connectCalendar]: this.connectCalendarStore,
      [AgentBookAppointmentStepsRoutes.actionsAgent]: this.agentActionsStore,
      [AgentBookAppointmentStepsRoutes.activateAgent]: this.agentActivateStore,
    }
  }

  get currentStepStore() {
    return this.stepStoreMap[this.currentStep] ?? this.settingAgentStore
  }

  setStepLoading = (loading: boolean) => {
    this._stepLoading = loading
  }

  setAgentData = (data: Partial<IAgentData>) => {
    const currentData = this._dirtyAgentData
    if (!currentData) {
      this._dirtyAgentData = {
        ...(data as IChatbotFullData),
      }
      return
    }

    this._dirtyAgentData = {
      ...currentData,
      ...data,
      settings: {
        ...currentData.settings,
        ...data.settings,
      },
    }
  }

  serializeToAgentPayload = (): IAgentPayload => {
    const data = this._dirtyAgentData || ({} as IAgentData)
    const settings = data.settings
    const advancedGeneralFields: (keyof IChatbotSettings)[] = [
      'followup_window',
      'enable_followup',
      'followup_assertiveness',
      'respond_tone',
      'respond_message_length',
    ]

    const payload: IAgentPayload = {
      general: {
        type: data.type || ChatbotInstructionType.BookAppointment,
        is_active: data.is_active || false,
        settings: {
          current_step: data.settings?.current_step || { value: '' },
        },
      },
    }

    if (data.name || data.first_message || data.settings?.use_knowledge_base) {
      payload.step_1 = {
        name: data.name || '',
        first_message: data?.first_message
          ? {
              ...data?.first_message,
              message: data?.first_message.type === 'manual' ? data.first_message?.message : '',
            }
          : null,
        settings: {
          use_knowledge_base: data.settings?.use_knowledge_base || null,
        },
        knowledge_base_documents: data.knowledge_base_documents || null,
      }
    }

    if (settings?.book_appointment_type || settings?.book_appointment_link) {
      payload.step_2 = {
        settings: {
          book_appointment_type: settings?.book_appointment_type || null,
          book_appointment_link: settings?.book_appointment_link || null,
          smart_booking_enabled: settings?.smart_booking_enabled || null,
          booking_provider: settings?.booking_provider || null,
        },
      }
    }

    const hasStep3Data = Boolean(
      data.success_actions?.length ||
        data.fallback_actions?.length ||
        data.qualified_actions?.length ||
        data.disqualified_actions?.length ||
        data.fields?.length ||
        settings?.skip_known_fields ||
        settings?.qualify_enabled ||
        settings?.qualify_instruction ||
        settings?.disqualify_behavior ||
        settings?.data_collection_instruction
    )

    if (hasStep3Data) {
      payload.step_3 = {
        success_actions: data.success_actions || null,
        fallback_actions: data.fallback_actions || null,
        qualified_actions: data.qualified_actions || null,
        disqualified_actions: data.disqualified_actions || null,
        fields: data.fields,
        settings: {
          skip_known_fields: settings?.skip_known_fields || null,
          data_collection_instruction: settings?.data_collection_instruction || null,
          qualify_instruction: settings?.qualify_instruction || null,
          qualify_enabled: settings?.qualify_enabled || null,
          disqualify_behavior: settings?.disqualify_behavior || null,
        },
      }
    }

    if (data.filters || data.send_from) {
      payload.step_4 = {
        filters: data.filters || null,
        send_from: data.send_from || null,
        settings: {
          allow_re_entering: settings?.allow_re_entering || null,
        },
      }
    }

    if (data?.advanced_general || advancedGeneralFields.some((field) => settings?.[field])) {
      const advancedGeneralSettings: IAgentAdvancedGeneralPayload['settings'] = {
        ...(settings?.followup_window && { followup_window: settings.followup_window }),
        ...(settings?.enable_followup && { enable_followup: settings.enable_followup }),
        ...(settings?.followup_assertiveness && {
          followup_assertiveness: settings.followup_assertiveness,
        }),
        ...(settings?.respond_tone && { respond_tone: settings.respond_tone }),
        ...(settings?.respond_message_length && {
          respond_message_length: settings.respond_message_length,
        }),
        ...data?.advanced_general?.settings,
      }

      payload.advanced_general = {
        settings: advancedGeneralSettings,
      }
    }

    if (data?.advanced_prompt || settings?.instructions) {
      const advancedPromptSettings: IAgentAdvancedPromptPayload['settings'] = {
        ...(settings?.instructions && { instructions: settings.instructions }),
        ...data?.advanced_prompt?.settings,
      }
      payload.advanced_prompt = {
        settings: advancedPromptSettings,
      }
    }

    return payload
  }

  handleSaveDraft = async (shouldRedirect: boolean, id?: number) => {
    const payload = this.serializeToAgentPayload()

    try {
      const { data } = id
        ? await ChatbotApi.updateAgent(Number(id), payload)
        : await ChatbotApi.createNewAgent(payload)
      if (data) {
        this.chatbot.syncOrigin(data)
        this.updateCache()
        this.agentNavigationStore.setAgentId(data.id)
        showToast({
          type: 'info',
          title: 'Changes saved',
        })

        if (shouldRedirect) {
          uiStore.changeRoute({
            path: this.agentNavigationStore.getCurrentUrl,
          })
        }
      }
    } catch (error) {
      logger.error(error)
      showToast({
        type: 'error',
        title: 'Failed to save changes',
      })
      throw error
    }
  }

  syncAllStepsToDirtyData = () => {
    this.setAgentData(this.createStepData('step_1'))
    this.setAgentData(this.createStepData('step_2'))
    this.setAgentData(this.createStepData('step_3'))
  }

  handleActivateAgent = async (
    id?: number,
    options?: { isPublished?: boolean; shouldRedirect?: boolean }
  ) => {
    const payload = {
      ...this.serializeToAgentPayload(),
      ...this.advancedSettingsStore.payload,
      ...this.advancedSettingsStore.promptSettingsStore.payload,
    }

    const { data } = id
      ? await ChatbotApi.updateAgent(Number(id), payload)
      : await ChatbotApi.createNewAgent(payload)

    if (data.is_active) {
      await chatbotOperation.enroll(data.id, data.name, data.filters, options?.isPublished)
      showToast({
        type: 'success',
        title: 'Agent activated successfully!',
      })

      if (options?.shouldRedirect) {
        uiStore.changeRoute({
          path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}`,
        })
      }
    }
  }

  getSettingsStep = (path: AgentBookAppointmentStepsRoutes) => {
    return this.agentNavigationStore.getStepByPath(path)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: this.agentNavigationStore.getLastUrl,
    })
  }

  handleNextStep = () => {
    uiStore.changeRoute({
      path: this.agentNavigationStore.getNextUrl,
    })
  }

  updateCache = () => {
    this._cacheParams = cloneDeep(this.params)
  }

  createStepData = (
    currentStep: string,
    additionalData?: Partial<IAgentData>
  ): Partial<IAgentData> => {
    const baseData = {
      type: ChatbotInstructionType.BookAppointment,
      is_active: false,
      settings: {
        current_step: { value: currentStep },
      },
    }
    const stepSpecificData = this.getStepSpecificData(currentStep)

    return {
      ...baseData,
      ...stepSpecificData,
      ...additionalData,
      settings: {
        ...baseData.settings,
        ...(stepSpecificData as Partial<IAgentData>)?.settings,
        ...additionalData?.settings,
      },
    }
  }

  validateKnowledgeBaseDocuments = (): boolean => {
    if (!this.chatbot.settings.knowledgeBaseUsage) return true

    return this.agentKnowledgeBaseStore.checkValidate()
  }

  quickSaveDraft = async (
    currentStep: string,
    options?: IAgentBookAppointmentStepsStoreOptions
  ) => {
    const { shouldRedirect = false, id, additionalData } = options || {}

    this.setAgentData(this.createStepData('step_1'))
    const agentData = this.createStepData(currentStep, additionalData)
    this.setAgentData(agentData)
    await this.handleSaveDraft(shouldRedirect, id)
  }

  handleStepValidationAndNavigation = async (
    currentStep: string,
    stepPayload: IParamsValidateAgentByStep,
    id?: string
  ) => {
    const step = currentStep
    const payload = stepPayload

    const { data } = id
      ? await ChatbotApi.validateExistingAgentByStep(step, Number(id), payload)
      : await ChatbotApi.validateNewAgentByStep(step, payload)

    if (data.valid) {
      const stepData = this.createStepData(currentStep)
      this.setAgentData(stepData)
      this.handleNextStep()
    }
  }

  handleApiError = (error: unknown, errorHandler?: (errors: IHandleError) => void) => {
    if (error instanceof AxiosError) {
      const apiErrors = error.response?.data?.errors as ISaveDraftErrorResponse

      Object.entries(apiErrors || {}).forEach(([, stepValue]) => {
        const stepErrors = (stepValue as { message: string; errors: IHandleError }).errors
        errorHandler?.(stepErrors)
      })
    }

    logger.error(error)
  }

  handleValidationApiError = (e: unknown, errorHandler?: (errors: IHandleError) => void) => {
    if (e instanceof AxiosError) {
      errorHandler?.(e.errors.toRecord())
    }

    logger.error(e)
  }

  private getStepSpecificData = (currentStep: string) => {
    switch (currentStep) {
      case 'step_1':
        return {
          ...this.settingAgentStore.stepPayload,
        }
      case 'step_2':
        return {
          ...this.connectCalendarStore.stepPayload,
        }
      case 'step_3':
        return {
          ...this.agentActionsStore.stepPayload,
        }
      case 'step_4':
        return {
          ...this.agentActivateStore.stepPayload,
        }
      default:
        return {}
    }
  }

  setAdvancedSettings = () => {
    const settings = {
      ...this._dirtyAgentData?.settings,
      ...this._dirtyAgentData?.advanced_general?.settings,
      ...this._dirtyAgentData?.advanced_prompt?.settings,
    }

    this._advancedSettingsStore.init(settings)
    this._advancedSettingsStore.openSettingsModal()
  }

  confirmLeavePage = async (
    blocker: Blocker,
    id?: string,
    handleSaveCallback?: (id?: string, shouldRedirect?: boolean) => Promise<boolean> | boolean
  ) => {
    if (blocker.state !== 'blocked') return

    const modalId = nanoid()

    const handleSave = async () => {
      try {
        const result = await handleSaveCallback?.(id, false)
        if (result) {
          onNext()
        } else {
          modalStore.closeModal(modalId)
        }
      } catch (error) {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again',
        })
        logger.error(error)
        modalStore.closeModal(modalId)
      }
    }

    const onNext = () => {
      const path = blocker.location.pathname
      blocker.proceed()

      uiStore.setRoutesBlocker(null)
      uiStore.changeRoute({
        path,
        type: 'vue',
        method: 'replace',
      })
      modalStore.closeModal(modalId)
    }

    modalStore.addModal({
      id: modalId,
      width: 280,
      type: ModalTypeList.WARNING,
      title: `Save Agent${this.chatbot?.name ? ` "${this.chatbot.name}" ` : ' '}before closing`,
      primaryAction: {
        text: this.chatbot.isActive ? 'Publish changes' : 'Save as draft',
        onAction: handleSave,
      },
      additionalSecondaryAction: {
        text: "Don't save",
        onAction: onNext,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(modalId)
        },
      },
    })
  }

  saveAgentBeforeTest = async (options?: IAgentBookAppointmentStepsStoreOptions) => {
    const getCurrentStepFromPathname = (): string => {
      const pathname = uiStore.location?.pathname

      if (!pathname) return AgentSteps.STEP1

      const currentStep = Object.entries(AgentBookAppointmentRouteToStepMap).find(([route]) =>
        pathname.includes(route)
      )
      return currentStep?.[1] || AgentSteps.STEP1
    }

    const currentStep = getCurrentStepFromPathname()
    if (!this.settingAgentStore.checkValidate()) return

    try {
      runInAction(() => {
        this._stepLoading = true
      })

      const confirmed = await chatbotOperation.confirmTest(this.chatbot.name, this.chatbot.isActive)

      if (!confirmed) return false
      // in case we call the Test Agent, but didn't save the advancedSettings separately
      const isSettingsValid = this.advancedSettingsStore.saveAndValidateSettings()
      if (!isSettingsValid) return false

      this.advancedSettingsStore.setOriginData({
        ...this.advancedSettingsStore.payload.advanced_general?.settings,
        ...this.advancedSettingsStore.promptSettingsStore.payload.advanced_prompt?.settings,
      })

      if (this.chatbot.isActive) {
        await this.agentActivateStore.handleActivateClickWithModal(options?.id?.toString(), {
          isPublished: true,
          shouldRedirect: false,
        })
      } else {
        await this.quickSaveDraft(currentStep, options)
      }

      if (!options?.id) {
        uiStore.changeRoute({
          path: this.agentNavigationStore.getCurrentUrl,
          options: {
            state: {
              test: true,
            },
          },
        })
      }

      return true
    } catch (e) {
      this.handleApiError(e, (errors) => {
        if (errors?.name) {
          this.chatbotErrors.sync({
            name: Array.isArray(errors.name) ? errors.name : [errors.name],
          })
        } else {
          Object.entries(errors)?.forEach(([, errorMessages]) => {
            toastStore.add({
              type: 'error',
              title: Array.isArray(errorMessages) ? errorMessages[0] : errorMessages,
            })
          })
        }
      })
      return false
    } finally {
      runInAction(() => {
        this._stepLoading = false
      })
    }
  }

  dispose = () => {
    this._settingAgentStore.dispose()
    this._connectCalendarStore.dispose()
    this._advancedSettingsStore.dispose()
    this._agentActionsStore.dispose()
    this._agentActivateStore.dispose()
  }
}
